import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Card,
  CardGrid,
  EmptyState,
  Filters,
  Page,
  PageHeader,
  Pagination,
  StatCard,
  StatCardList,
  Toolbar,
} from '@evogard/ui';
import { Chart } from '@evogard/ui/chart';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import {
  PLATE_CLASSIFICATIONS,
  count,
  format,
  loadFailureMessage,
  type Money,
} from '@evogard/domain';
import type {
  Period,
  PlateTally,
  ReasonNotToSettle,
  SalesPanel,
  SalesPanelConsultant,
  SalesPanelFigures,
} from '@evogard/contract';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import {
  CLASSIFICATION_LABEL,
  MoneyValue,
  ProfileBadge,
  WhyNotSettled,
  currentPeriod,
  periodLabel,
} from './common.js';
import { ComparisonView } from './SalesComparisonView.js';
import {
  VariationBadge,
  centsOf,
  compactMoney,
  formatPercent,
  lastDayOfPeriod,
  variationOf,
} from './reportFormat.js';

/**
 * O Relatório de Vendas — `/sales/reports`.
 *
 * ## Para quem, e sobre quem
 *
 * Para quem conduz o comercial — liderança e administração —, sobre o comercial
 * INTEIRO da base. O Evoplan só conhecia o time interno; aqui entram todos os
 * perfis de consultor, e também a venda que ainda não casou com consultor
 * nenhum, que é venda da praça e fica no total. A venda é a de
 * `app.sales_sales`: a registrada à mão e a importada do ERP, na mesma tabela
 * que o ranking, o painel do time e a apuração leem.
 *
 * ## Duas abas, um recorte
 *
 * **Painel** (a competência, contra o mês anterior nos mesmos dias) e
 * **Comparação de meses** (`SalesComparisonView`: duas competências quaisquer
 * lado a lado). Consultor, time e dias moram no mesmo endereço e valem para as
 * duas — trocar de aba não perde o recorte.
 *
 * ## Nenhuma conta mora aqui
 *
 * Os totais, as categorias, a contagem da casa e o atingimento das metas chegam
 * do servidor (`shapeSalesPanel`). A tela faz só duas aritméticas, e as duas
 * são de APRESENTAÇÃO: a variação percentual contra o mês anterior e a soma das
 * metas dos times, que ela diz que é uma soma.
 *
 * ## O que o desenho de referência pedia e NÃO está aqui
 *
 * Valor líquido de adesão, mensalidade, canal, origem, leads e conversão. Nenhum
 * tem casa no banco hoje — o docblock de `salesPanelSchema` diz por quê, um a
 * um. Pela regra 14 ficam de fora inteiros, e a tela não reserva o lugar deles
 * com zero.
 */
export function SalesReportScreen() {
  const search = useSearch({ from: '/authenticated/sales/reports' });
  const navigate = useNavigate();

  return (
    <Page measure="cheia">
      <PageHeader title="Relatório de Vendas">
        O comercial inteiro da base na competência — todos os perfis de consultor, a venda
        registrada à mão e a importada do ERP.
      </PageHeader>

      <Tabs
        value={search.tab ?? 'painel'}
        onValueChange={(value) =>
          void navigate({
            to: '/sales/reports',
            search: {
              ...search,
              tab: value === 'comparacao' ? 'comparacao' : undefined,
            },
            replace: true,
          })
        }
      >
        <TabsList aria-label="O que ver no relatório">
          <TabsTrigger value="painel">Painel</TabsTrigger>
          <TabsTrigger value="comparacao">Comparação de meses</TabsTrigger>
        </TabsList>

        <TabsContent value="painel" className="flex flex-col gap-5 pt-5">
          <PanelView />
        </TabsContent>

        <TabsContent value="comparacao" className="flex flex-col gap-5 pt-5">
          <ComparisonView />
        </TabsContent>
      </Tabs>
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * O Painel
 * ------------------------------------------------------------------------- */

function PanelView() {
  const { data: context } = useSessionContext();
  const search = useSearch({ from: '/authenticated/sales/reports' });
  const navigate = useNavigate();
  const tenantId = context?.currentTenant.id;

  const period = (search.month ?? currentPeriod()) as Period;
  const weeks = weeksOf(period);
  const chosenWeeks = (search.weeks ?? []).filter((w) => w <= weeks.length);
  const chosenDays = (search.days ?? []).filter((d) => d <= lastDayOf(period));
  /* O recorte que a API recebe é o conjunto de dias — a semana é só um atalho. */
  const days = [
    ...new Set([...chosenWeeks.flatMap((w) => weeks[w - 1]?.days ?? []), ...chosenDays]),
  ].sort((a, b) => a - b);

  const query = useQuery({
    queryKey: [
      'comercial',
      'relatorio',
      'painel',
      tenantId,
      period,
      search.consultant,
      search.team,
      days.join(','),
    ],
    enabled: context !== undefined,
    queryFn: async () => {
      const r = await api.sales.salesPanel({
        query: {
          period,
          consultantId: search.consultant,
          teamId: search.team,
          days: days.length === 0 ? undefined : days.join(','),
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o painel de vendas'));
      }
      return r.body;
    },
    /*
     * Segura o painel anterior só dentro da mesma base: os números da CE sob o
     * nome da GO seriam uma afirmação sobre o desempenho de outra praça.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? previous : undefined,
  });

  const panel = query.data;
  const options = panel?.options;

  return (
    <>
      <Toolbar
        toolbar={
          <Filters
            values={{
              month: search.month ?? '',
              consultant: search.consultant ? String(search.consultant) : '',
              team: search.team ? String(search.team) : '',
              weeks: chosenWeeks.map(String),
              days: chosenDays.map(String),
            }}
            onApply={(values) => {
              const month = String(values.month ?? '');
              const consultant = String(values.consultant ?? '');
              const team = String(values.team ?? '');
              const nextWeeks = arrayOf(values.weeks).map(Number);
              const nextDays = arrayOf(values.days).map(Number);
              void navigate({
                to: '/sales/reports',
                search: {
                  tab: search.tab,
                  month: month === '' ? undefined : month,
                  consultant: consultant === '' ? undefined : Number(consultant),
                  /* Consultor escolhido vence o time: a pessoa já diz de quem se fala. */
                  team: team === '' || consultant !== '' ? undefined : Number(team),
                  /* Mês trocado leva as semanas e os dias junto: elas são daquele mês. */
                  weeks:
                    month !== (search.month ?? '') || nextWeeks.length === 0
                      ? undefined
                      : nextWeeks,
                  days:
                    month !== (search.month ?? '') || nextDays.length === 0
                      ? undefined
                      : nextDays,
                },
                replace: true,
              });
            }}
            fields={[
              {
                type: 'periodo',
                name: 'month',
                label: 'Mês',
                help: 'Vazio volta ao mês de hoje.',
              },
              {
                type: 'selecao',
                name: 'consultant',
                label: 'Consultor',
                placeholder: 'Todos os consultores',
                help: 'Escolhido o consultor, o time é ignorado.',
                options: (options?.consultants ?? []).map((c) => ({
                  value: String(c.id),
                  label: c.nome,
                })),
              },
              {
                type: 'selecao',
                name: 'team',
                label: 'Time',
                placeholder: 'Todos os times',
                help: 'Pela composição de hoje do time.',
                options: (options?.teams ?? []).map((t) => ({
                  value: String(t.id),
                  label: t.nome,
                })),
              },
              {
                type: 'multipla',
                name: 'weeks',
                label: 'Semanas',
                placeholder: 'Todas as semanas',
                help: 'De segunda a domingo, dentro do mês.',
                options: weeks.map((w) => ({
                  value: String(w.week),
                  label: weekLabel(w),
                })),
              },
              {
                type: 'multipla',
                name: 'days',
                label: 'Dias do mês',
                placeholder: 'Todos os dias',
                help: 'Somados às semanas escolhidas.',
                options: Array.from({ length: lastDayOf(period) }, (_, i) => ({
                  value: String(i + 1),
                  label: String(i + 1).padStart(2, '0'),
                })),
              },
            ]}
          />
        }
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {query.isPending && !query.isError && (
        <Card title="Carregando…">
          <span className="esqueleto block h-4 w-2/3" />
          <span className="esqueleto mt-2 block h-4 w-1/2" />
        </Card>
      )}

      {panel !== undefined && <PanelBody panel={panel} filteredDays={days.length > 0} />}
    </>
  );
}

function PanelBody({
  panel,
  filteredDays,
}: {
  panel: SalesPanel;
  filteredDays: boolean;
}) {
  const comparison = comparisonLabel(panel, filteredDays);
  const nothing =
    totalOf(panel.current) === 0 &&
    totalOf(panel.previous) === 0 &&
    panel.withoutDay === 0;

  return (
    <>
      <GoalCard panel={panel} />

      {nothing ? (
        <EmptyState
          icon="comercial"
          title={`Nenhuma venda em ${periodLabel(panel.period)}.`}
        >
          Nem neste recorte, nem no mês anterior. A venda chega pela importação do ERP
          (Comercial › Vendas › Importar) ou registrada à mão, e aparece aqui assim que
          existir.
        </EmptyState>
      ) : (
        <>
          <section aria-labelledby="relatorio-categorias" className="flex flex-col gap-3">
            <h2 id="relatorio-categorias" className="text-base font-semibold text-texto">
              Vendas por categoria
            </h2>
            <StatCardList>
              <StatCard
                icon="grafico"
                label="Contam para a casa"
                value={panel.current.counted}
                support={
                  <Trend
                    current={panel.current.counted}
                    previous={panel.previous.counted}
                    comparison={comparison}
                  />
                }
                footer="Venda, troca e Plano Bronze — a contagem do ranking."
              />
              {PLATE_CLASSIFICATIONS.map((classification) => (
                <StatCard
                  key={classification}
                  icon={CLASSIFICATION_ICON[classification]}
                  label={CLASSIFICATION_LABEL[classification]}
                  value={quantityOf(panel.current.plates, classification)}
                  support={
                    <Trend
                      current={quantityOf(panel.current.plates, classification)}
                      previous={quantityOf(panel.previous.plates, classification)}
                      comparison={comparison}
                    />
                  }
                />
              ))}
              {panel.current.unclassified > 0 && (
                <StatCard
                  icon="atencao"
                  tone="alerta"
                  label="Sem classificação"
                  value={panel.current.unclassified}
                  support="Fora das quatro categorias até alguém classificar."
                />
              )}
            </StatCardList>
          </section>

          <AdhesionSection panel={panel} comparison={comparison} />

          <VehicleTypes panel={panel} />

          <ConsultantSummary panel={panel} />
        </>
      )}
    </>
  );
}

/* --- a meta ------------------------------------------------------------- */

/**
 * A meta do mês — a SOMA das metas de time, porque a casa não tem meta da base.
 *
 * O atingimento de cada time chega pronto, pela mesma função da tela de Metas.
 * Se um deles não pôde ser medido, o total também não pode: somar só os que
 * mediram diria que a praça está mais longe da meta do que está.
 */
function GoalCard({ panel }: { panel: SalesPanel }) {
  const month = periodLabel(panel.period);
  const goals = panel.goals.filter((g) => g.quantity !== null);

  if (goals.length === 0) {
    return (
      <Card
        title="Quantidade de vendas para bater a meta"
        descricao="A meta do mês é a soma das metas em quantidade dos times."
      >
        <p className="text-sm text-texto-suave">
          Nenhum time tem meta em quantidade em {month}. As metas são definidas em{' '}
          <strong className="font-medium text-texto">Comercial › Metas</strong>, por time
          e por competência.
        </p>
      </Card>
    );
  }

  const target = goals.reduce((acc, g) => acc + (g.quantity ?? 0), 0);
  const refused = goals.filter((g) => g.attainment.estado === 'nao_apuravel');
  const reasons = [
    ...new Set(
      refused.flatMap((g) =>
        g.attainment.estado === 'nao_apuravel' ? g.attainment.reasons : [],
      ),
    ),
  ] as ReasonNotToSettle[];
  const achieved =
    refused.length > 0
      ? null
      : goals.reduce(
          (acc, g) =>
            acc + (g.attainment.estado === 'medido' ? g.attainment.quantity : 0),
          0,
        );
  const ratio = achieved === null || target === 0 ? null : achieved / target;

  return (
    <Card
      title="Quantidade de vendas para bater a meta"
      descricao={`A soma das metas em quantidade de ${count(goals.length, 'time', 'times')} em ${month}, e o que conta para cada uma pela régua do perfil de quem vendeu. Não segue os filtros de consultor, time e dia.`}
    >
      <div className="flex flex-col gap-4">
        {refused.length > 0 && (
          <WhyNotSettled
            title={`A meta de ${count(refused.length, 'time', 'times')} não pôde ser medida, e por isso o total também não.`}
            reasons={reasons}
          />
        )}

        <StatCardList>
          <StatCard
            icon="bandeira"
            label="Meta geral"
            value={target}
            support={`soma de ${count(goals.length, 'time', 'times')}`}
          />
          <StatCard
            icon="grafico"
            label="Contam para a meta"
            value={achieved ?? '—'}
            support={
              achieved === null ? 'não medido — ver o aviso acima' : 'no mês inteiro'
            }
          />
          <StatCard
            icon="confirmado"
            label="Faltam"
            value={achieved === null ? '—' : Math.max(target - achieved, 0)}
            support={
              achieved !== null && achieved >= target
                ? 'meta batida'
                : 'para a meta geral'
            }
            tone={achieved !== null && achieved >= target ? 'sucesso' : 'neutro'}
          />
        </StatCardList>

        {ratio !== null && <ProgressBar label="Progresso da meta geral" ratio={ratio} />}

        <ul className="flex flex-col divide-y divide-borda-suave">
          {goals.map((g) => {
            const done = g.attainment.estado === 'medido' ? g.attainment.quantity : null;
            return (
              <li
                key={g.team.id}
                className="grid grid-cols-1 items-center gap-2 py-2.5 sm:grid-cols-[12rem_1fr_7rem]"
              >
                <span className="truncate text-sm font-medium text-texto">
                  {g.team.nome}
                </span>
                {done === null ? (
                  <span className="text-xs text-texto-suave">não medido</span>
                ) : (
                  <Bar ratio={g.quantity ? done / g.quantity : 0} />
                )}
                <span className="text-sm tabular-nums text-texto sm:text-right">
                  {done ?? '—'} <span className="text-texto-suave">/ {g.quantity}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}

/* --- a adesão ----------------------------------------------------------- */

interface DailyPoint {
  label: string;
  plates: number;
  adhesion: number;
}

function AdhesionSection({
  panel,
  comparison,
}: {
  panel: SalesPanel;
  comparison: string;
}) {
  const month = panel.period.slice(5, 7);
  const current = centsOf(panel.current.adhesion);
  const previous = centsOf(panel.previous.adhesion);
  const points: DailyPoint[] = panel.daily.map((d) => ({
    label: `${String(d.day).padStart(2, '0')}/${month}`,
    plates: d.plates,
    adhesion: centsOf(d.adhesion),
  }));

  return (
    <section aria-labelledby="relatorio-adesao" className="flex flex-col gap-3">
      <h2 id="relatorio-adesao" className="text-base font-semibold text-texto">
        Entrada de adesões
      </h2>
      <StatCardList>
        <StatCard
          icon="dinheiro"
          label="Adesão bruta"
          value={format(current)}
          support={
            <Trend current={current} previous={previous} comparison={comparison} />
          }
          footer={
            panel.current.withoutAmount > 0
              ? `${count(panel.current.withoutAmount, 'venda sem valor de adesão fica', 'vendas sem valor de adesão ficam')} fora da soma.`
              : 'O valor de adesão que cada venda declara.'
          }
        />
      </StatCardList>

      <CardGrid>
        <Chart<DailyPoint>
          kind="linha"
          title="Adesão bruta por dia"
          descricao={`A soma da adesão das vendas de cada dia de ${periodLabel(panel.period)}, pela data de contrato.`}
          label={`Adesão bruta por dia em ${periodLabel(panel.period)}`}
          data={points}
          empty={`Nenhuma venda com dia conhecido em ${periodLabel(panel.period)}.`}
          category={(p) => p.label}
          series={[{ key: 'adhesion', label: 'Adesão bruta', value: (p) => p.adhesion }]}
          format={(cents) => format(cents as Money)}
          axisFormat={compactMoney}
        />
        <Chart<DailyPoint>
          kind="barra"
          title="Placas por dia"
          descricao="Todas as categorias, inclusive a reativação e a venda sem classificação."
          label={`Placas por dia em ${periodLabel(panel.period)}`}
          data={points}
          empty={`Nenhuma venda com dia conhecido em ${periodLabel(panel.period)}.`}
          category={(p) => p.label}
          series={[{ key: 'plates', label: 'Placas', value: (p) => p.plates }]}
        />
      </CardGrid>
    </section>
  );
}

/* --- o tipo de veículo ---------------------------------------------------- */

function VehicleTypes({ panel }: { panel: SalesPanel }) {
  const rows = panel.vehicleTypes.map((t) => ({
    label: t.label ?? 'Sem veículo no cadastro',
    plates: t.plates,
    adhesion: centsOf(t.adhesion),
  }));

  return (
    <section aria-labelledby="relatorio-veiculos" className="flex flex-col gap-3">
      <h2 id="relatorio-veiculos" className="text-base font-semibold text-texto">
        Por tipo de veículo
      </h2>
      <CardGrid>
        <Chart<(typeof rows)[number]>
          kind="pizza"
          title="Quantidade por veículo"
          descricao="As placas vendidas no recorte, pelo tipo que o ERP dá ao veículo."
          label="Placas por tipo de veículo"
          data={rows}
          empty="Nenhuma placa no recorte."
          slice={{ label: (r) => r.label, value: (r) => r.plates }}
        />
        <Chart<(typeof rows)[number]>
          kind="pizza"
          title="Adesão por veículo"
          descricao="A adesão bruta das mesmas placas, pelo mesmo tipo."
          label="Adesão bruta por tipo de veículo"
          data={rows}
          empty="Nenhuma adesão declarada no recorte."
          slice={{ label: (r) => r.label, value: (r) => r.adhesion }}
          format={(cents) => format(cents as Money)}
        />
      </CardGrid>
    </section>
  );
}

/* --- o resumo por consultor ---------------------------------------------- */

/**
 * O resumo por consultor, paginado NA TELA.
 *
 * A rota devolve o recorte inteiro — é sobre ele que a ordem e a barra relativa
 * são medidas —, e a página só escolhe quais linhas desenhar. Paginar no
 * servidor faria a barra de "quem mais contou" mudar de régua a cada página.
 *
 * Trocar o recorte volta para a página 1: quem mudou o filtro estando na página
 * 4 cairia numa página que talvez nem exista mais.
 */
function ConsultantSummary({ panel }: { panel: SalesPanel }) {
  const top = Math.max(1, ...panel.consultants.map((c) => c.counted));
  const [perPage, setPerPage] = useState(10);
  const [paging, setPaging] = useState({ key: '', page: 1 });
  const key = `${panel.period}|${panel.consultants.map((c) => c.consultant.id).join(',')}`;
  const page = paging.key === key ? paging.page : 1;
  const rows = panel.consultants.slice((page - 1) * perPage, page * perPage);

  return (
    <Card
      title="Resumo por consultor"
      descricao="Todo consultor ativo do recorte, de qualquer perfil — inclusive quem não vendeu no mês. A barra é a contagem da casa, relativa a quem mais contou."
      bleed
    >
      {panel.consultants.length === 0 ? (
        <p className="px-5 py-4 text-sm text-texto-suave">
          Nenhum consultor cadastrado neste recorte.
        </p>
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-borda-suave">
            {rows.map((row) => (
              <ConsultantRow
                key={row.consultant.id}
                row={row}
                ratio={row.counted / top}
              />
            ))}
          </ul>
          <Pagination
            className="border-t border-borda-suave px-5 py-3"
            page={page}
            perPage={perPage}
            total={panel.consultants.length}
            label="consultores"
            onChange={(next) => setPaging({ key, page: next })}
            onPerPageChange={(next) => {
              setPerPage(next);
              setPaging({ key, page: 1 });
            }}
          />
        </>
      )}
    </Card>
  );
}

function ConsultantRow({ row, ratio }: { row: SalesPanelConsultant; ratio: number }) {
  const breakdown = PLATE_CLASSIFICATIONS.map(
    (c) => `${CLASSIFICATION_LABEL[c]} ${quantityOf(row.plates, c)}`,
  );
  if (row.unclassified > 0) breakdown.push(`Sem classificação ${row.unclassified}`);

  return (
    <li className="grid grid-cols-1 gap-2 px-5 py-3 lg:grid-cols-[16rem_1fr_9rem] lg:items-center">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-sm font-semibold text-texto">
          {row.consultant.user.nome}
        </span>
        <span className="flex flex-wrap items-center gap-1.5 text-xs text-texto-suave">
          {row.team?.nome ?? 'Sem time'}
          <ProfileBadge profile={row.profile} />
        </span>
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <Bar ratio={ratio} />
        <span className="text-xs text-texto-suave">{breakdown.join(' · ')}</span>
      </div>

      <div className="flex flex-col lg:items-end">
        <span className="text-sm font-semibold tabular-nums text-texto">
          {count(row.counted, 'contada', 'contadas')}
        </span>
        <span className="text-xs text-texto-suave">
          <MoneyValue value={row.adhesion} />
          {row.withoutAmount > 0 && ` · ${row.withoutAmount} sem valor`}
        </span>
      </div>
    </li>
  );
}

/* --- as peças ----------------------------------------------------------- */

function ProgressBar({ label, ratio }: { label: string; ratio: number }) {
  const percentage = ratio * 100;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs font-semibold uppercase text-texto-suave">
        <span>{label}</span>
        <span className="tabular-nums text-texto">{formatPercent(percentage)}</span>
      </div>
      <Bar ratio={ratio} tall />
    </div>
  );
}

/** A barra de proporção — preenchimento da primária, nunca texto. */
function Bar({ ratio, tall = false }: { ratio: number; tall?: boolean }) {
  const width = Math.max(0, Math.min(ratio, 1)) * 100;
  return (
    <div
      className={`w-full overflow-hidden rounded-pilula bg-superficie-suave ${tall ? 'h-2.5' : 'h-1.5'}`}
      aria-hidden="true"
    >
      <div className="h-full rounded-pilula bg-primaria" style={{ width: `${width}%` }} />
    </div>
  );
}

/**
 * A variação contra o mês anterior. É aritmética de apresentação — os dois
 * números vieram do servidor, no mesmo recorte —, e mês anterior zerado não tem
 * variação: "+∞%" seria um número com cara de medido.
 */
function Trend({
  current,
  previous,
  comparison,
}: {
  current: number;
  previous: number;
  comparison: string;
}) {
  const change = variationOf(current, previous);
  if (change === null) {
    return <span className="text-texto-suave">nada {comparison} para comparar</span>;
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <VariationBadge change={change} />
      <span className="text-texto-suave">{comparison}</span>
    </span>
  );
}

// @valor
const CLASSIFICATION_ICON = {
  venda: 'carrinho',
  troca: 'atualizar',
  bronze: 'etiqueta',
  reativacao: 'historico',
} as const satisfies Record<(typeof PLATE_CLASSIFICATIONS)[number], string>;

/* --- aritmética de tela --------------------------------------------------- */

/** As semanas do mês, de segunda a domingo — a primeira e a última podem ser curtas. */
export function weeksOf(period: Period): { week: number; days: number[] }[] {
  const [year, month] = period.split('-').map(Number) as [number, number];
  const last = lastDayOf(period);
  const weeks: { week: number; days: number[] }[] = [];
  let current: number[] = [];
  for (let day = 1; day <= last; day++) {
    current.push(day);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    if (weekday === 0 || day === last) {
      weeks.push({ week: weeks.length + 1, days: current });
      current = [];
    }
  }
  return weeks;
}

function weekLabel(w: { week: number; days: number[] }): string {
  const first = w.days[0]!;
  const last = w.days[w.days.length - 1]!;
  return `Semana ${w.week} · ${first === last ? `dia ${first}` : `${first} a ${last}`}`;
}

function comparisonLabel(panel: SalesPanel, filteredDays: boolean): string {
  const previous = periodLabel(panel.previous.period);
  if (filteredDays) return `em ${previous}, nos mesmos dias`;
  if (panel.throughDay < lastDayOf(panel.period)) {
    return `em ${previous}, de 1 a ${panel.throughDay}`;
  }
  return `em ${previous}`;
}

const lastDayOf = lastDayOfPeriod;

function quantityOf(plates: readonly PlateTally[], classification: string): number {
  return plates.find((p) => p.classification === classification)?.quantity ?? 0;
}

function totalOf(figures: SalesPanelFigures): number {
  return figures.plates.reduce((acc, p) => acc + p.quantity, 0) + figures.unclassified;
}

function arrayOf(value: string | string[] | undefined): string[] {
  if (value === undefined) return [];
  return (Array.isArray(value) ? value : [value]).filter((v) => v !== '');
}
